import { BatteryCharging, Home, Power, Sun } from "lucide-react";
import type { Reading } from "@spf/contracts";
import { Card } from "../Card.js";
import { formatUnit } from "../../lib/format.js";

/**
 * The design's node tiles, carrying measurements instead of switches.
 *
 * The design's band here is a "Live Interactive Load Simulator": three buttons
 * that add 2.2 kW of car charging and 1.5 kW of heat pump to the figures on
 * screen, and a third that pretends the grid has failed. It is a convincing
 * demonstration and it is the single most dangerous thing in the design to
 * port, because the numbers it produces are indistinguishable from measured
 * ones on a page whose entire purpose is to say what is actually happening.
 * Someone would eventually read a simulated outage as a real one.
 *
 * So the tiles keep the design's shape and show the four nodes as the inverter
 * reports them, the panels among them since slice 5c (`DECISIONS/0028`). Nothing here is adjustable, because nothing here is a control:
 * this system does not write to the hardware (`DECISIONS/0004`).
 */
export function NodeTiles({ reading }: { reading: Reading }) {
  return (
    <Card title="Nodes">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={<Home size={30} aria-hidden="true" className="text-accent-2-ink" />}
          label="Home"
          value={formatUnit(reading.loadPowerW, "W", true)}
          note={
            reading.loadPercent === null
              ? "Drawn through the inverter."
              : `${String(reading.loadPercent)}% of what the inverter can carry.`
          }
        />
        <Tile
          icon={<BatteryCharging size={30} aria-hidden="true" className="text-accent-2-ink" />}
          label="Battery"
          value={formatUnit(Math.abs(reading.batteryPowerW), "W", true)}
          note={`${directionOf(reading.batteryPowerW)}, at ${String(reading.socPct)}%.`}
        />
        <Tile
          icon={<Power size={30} aria-hidden="true" className="text-accent-ink" />}
          label="Grid"
          value={formatUnit(reading.gridPowerW, "W", true)}
          note={`Mains at ${formatUnit(reading.gridVoltageV, "V")}.`}
        />
        <Tile
          icon={<Sun size={30} aria-hidden="true" className="text-accent-ink" />}
          label="Panels"
          value={formatUnit(reading.pvPowerW, "W", true)}
          note={
            reading.pvVoltageV === null
              ? "Generated by the panels."
              : `The string at ${formatUnit(reading.pvVoltageV, "V")}.`
          }
        />
      </div>
    </Card>
  );
}

function Tile({
  icon,
  label,
  value,
  note,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="flex flex-col items-center rounded-3xl border border-line bg-line/30 p-5 text-center">
      <span className="mb-2">{icon}</span>
      <span className="text-xs font-medium text-ink-muted">{label}</span>
      <span className="tabular text-2xl font-bold text-ink">{value}</span>
      <span className="mt-1 text-[11px] text-ink-muted">{note}</span>
    </div>
  );
}

/**
 * Words rather than a signed number. The protocol's sign convention --
 * negative is charging -- is a fact about the wire and not something a reader
 * should have to hold in their head.
 */
function directionOf(batteryPowerW: number): string {
  if (batteryPowerW < 0) return "Charging";
  if (batteryPowerW > 0) return "Discharging";
  return "Idle";
}
